import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WIZARD_STEPS, type WizardStepMeta } from './wizardSteps';

export interface WizardStepRailProps {
  currentStep: number;
  /** Active step list (varies by connector type, e.g. Schedule omitted). */
  steps?: WizardStepMeta[];
  onStepClick?: (stepId: number) => void;
}

export const WizardStepRail: React.FC<WizardStepRailProps> = ({
  currentStep,
  steps = WIZARD_STEPS,
  onStepClick,
}) => {
  const total = steps.length;
  const completed = currentStep - 1;
  const pct = Math.round((completed / total) * 100);

  return (
    <nav aria-label="Wizard steps" className="space-y-4">
      <ol className="space-y-1">
        {steps.map((step) => {
          const isComplete = currentStep > step.id;
          const isActive = currentStep === step.id;
          const clickable = isComplete && !!onStepClick;

          const inner = (
            <span className="flex items-center gap-3">
              <span
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs',
                  isComplete && 'border-primary bg-primary text-primary-foreground',
                  isActive && 'border-primary text-primary',
                  !isComplete && !isActive && 'border-muted-foreground/30 text-muted-foreground'
                )}
              >
                {isComplete ? <Check className="h-4 w-4" /> : step.id}
              </span>
              <span className="flex flex-col text-left">
                <span className={cn('text-sm font-medium', isActive ? 'text-foreground' : 'text-muted-foreground')}>
                  {step.title}
                </span>
                <span className="text-xs text-muted-foreground">{step.description}</span>
              </span>
            </span>
          );

          return (
            <li key={step.id}>
              {clickable ? (
                <button
                  type="button"
                  onClick={() => onStepClick?.(step.id)}
                  className="w-full rounded-md px-2 py-2 hover:bg-muted/50"
                >
                  {inner}
                </button>
              ) : (
                <div aria-current={isActive ? 'step' : undefined} className="px-2 py-2">
                  {inner}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      <div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{pct}% complete</p>
      </div>
    </nav>
  );
};
